'use client';

import flow from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import { formatPrice, getNumberOfGuests, calculateNights, formatDateRange } from '@fshr/shared';
import { useTranslations } from '@fshr/context/i18n-context';
import { ShoppingCartItem } from './shopping-cart-item';
import { definitions } from '@micro-apps/env';
import HorizontalLine from '../../horizontal-line';
import { useMediaQuery } from 'react-responsive';
import { cn } from '@fshr/shared-react';
import { CheckoutContent, DialogOfferDataType, OfferProps } from '../../types';
import { RoomRateType } from '../../types/shopping-cart-item';
import BevSafeText from '../../bev-safe-text';

type ShoppingCartSummaryProps = {
	officialName: {
		__html: string;
	};
	booking?: definitions['booking'];
	checkoutContent?: CheckoutContent;
	isMultiRoom?: boolean;
	basket?: definitions['eventProduct'][];
	estimatedTotalDisclaimer: string;
	removeBooking?: () => void;
	removeFromCart?: (productId: string) => void;
	showPriceBreakdown?: () => void;
	ppMode?: boolean;
	hadleOpenDialog?: (args: { offerData: DialogOfferDataType }) => void;
};

export default function ShoppingCartSummary({
	officialName,
	booking,
	checkoutContent,
	estimatedTotalDisclaimer,
	ppMode,
	isMultiRoom = false,
}: ShoppingCartSummaryProps) {
	const t = useTranslations();
	const isLg = useMediaQuery({ minWidth: '960px' });

	const totalPrice = flow(get(['price', 'total', 'cash']), ({ currencyCode, amount }) =>
		formatPrice({ value: amount, currencyCode })
	)(booking);

	const propertyBillingTotalPrice = flow(
		get(['price', 'total', 'cash']),
		({ propertyBillingCurrencyCode, propertyBillingAmount }) => {
			if (!propertyBillingCurrencyCode && !propertyBillingAmount) return null;
			return formatPrice({
				value: propertyBillingAmount,
				currencyCode: propertyBillingCurrencyCode,
			});
		}
	)(booking);

	const sameCurrencyInPrices = flow(
		get(['price', 'total', 'cash']),
		({ currencyCode, propertyBillingCurrencyCode }) => currencyCode === propertyBillingCurrencyCode
	)(booking);

	const { checkInDate, checkOutDate } = booking?.hotelProducts?.[0] ?? {};

	const formattedStayDateRange =
		checkInDate && checkOutDate && formatDateRange(checkInDate, checkOutDate, true);

	const getOfferDetails: (props: {
		offerData?: OfferProps;
		roomRate?: RoomRateType;
		price?: number;
		currencyCode?: string;
		nights?: number;
		ppAmenities?: string[];
	}) => DialogOfferDataType | undefined = ({
		offerData,
		roomRate,
		price,
		currencyCode,
		nights,
		ppAmenities,
	}) => {
		if (!offerData || !roomRate) {
			return;
		}
		return {
			...offerData,
			beds: [
				{
					price: {
						amount: {
							withoutDecimal: Math.round(roomRate?.averageNightlyPrice?.cash?.amount ?? 0),
							withDecimal: roomRate?.averageNightlyPrice?.cash?.amount?.toString?.() ?? '0',
						},
						currency: roomRate?.averageNightlyPrice?.cash?.currencyCode ?? 'USD',
					},
				},
			],
			price: {
				label: t('Avg. nightly rates'),
				amount: {
					withoutDecimal: Math.round(roomRate?.averageNightlyPrice?.cash?.amount ?? 0),
					withDecimal: roomRate?.averageNightlyPrice?.cash?.amount?.toString?.() ?? '0',
				},
				currency: roomRate?.averageNightlyPrice?.cash?.currencyCode ?? 'USD',
			},
			estimatedTotal:
				price && nights
					? {
							amount: {
								withDecimal: price?.toString(),
								withoutDecimal: Math.round(price ?? 0),
							},
							currency: currencyCode,
							label: t('Est. total {0} nights', [nights.toString()]),
						}
					: undefined,
			cancellationPolicy: roomRate?.fsRoomOfferCancellationPolicy,
			title: roomRate?.ratePlanTitle,
			feesDisclaimer: roomRate?.feesDisclaimerCheckout || roomRate?.feesDisclaimer,
			disclaimer: roomRate?.fsRoomOfferDisclaimer,
			ppAmenities:
				ppAmenities && ppAmenities.length > 0
					? [
							{
								data: ppAmenities,
								title: 'Exclusive Preferred Partner Benefits',
							},
						]
					: undefined,
		};
	};

	return (
		<div
			className={cn('flex flex-col w-full', {
				'spacing-stack-l': isLg,
				'spacing-stack-xl': !isLg,
			})}
		>
			<div className="flex flex-col pb-[30px] px-[20px] lg:py-[50px] lg:px-[30px] bg-surface-raised-subtle gap-[40px] w-full">
				<div className="flex flex-col w-full">
					<div className="flex flex-col gap-[15px] justify-center items-center spacing-heading-m text-text-heading">
						{officialName?.__html && (
							<div className="text-h4">
								<div
									className="text-center"
									dangerouslySetInnerHTML={{ __html: officialName?.__html }}
								/>
							</div>
						)}

						{checkInDate && checkOutDate && (
							<div className="text-label">{formattedStayDateRange}</div>
						)}
					</div>

					<div
						className={cn('flex flex-col w-full', {
							'spacing-stack-m': isLg,
							'spacing-stack-l': !isLg,
						})}
					>
						<HorizontalLine additionalClasses="border-border-subtle" />
						{(booking?.hotelProducts?.length ?? 0) > 0 ? (
							<div className="flex flex-col spacing-stack-m w-full">
								{booking?.hotelProducts?.map((hotelProduct, index) => {
									const { guestCount, roomRate, roomTypes, price, productType } = hotelProduct;
									const { numberAdults = 0, numberChildren = 0 } = guestCount ?? {};
									return roomTypes?.map(roomType => {
										// If is a multiroom booking show the room number, otherwise show the stay title
										const itemTitle = isMultiRoom
											? `${t('Stay')} | ${t('Room')} ${index + 1}`
											: t('Stay');
										const totalRoomRatePrice = hotelProduct?.price?.prepay?.base?.cash?.amount;
										const taxDisclaimer = () =>
											booking.resortFeesEnabled
												? (hotelProduct?.roomRate?.feesDisclaimerCheckout ??
													hotelProduct?.roomRate?.feesDisclaimer)
												: hotelProduct?.roomRate?.feesDisclaimer || undefined;
										const offerData =
											roomRate?.ratePlanCode || roomRate?.orsRatePlanCode
												? (checkoutContent?.offerDetails?.[roomRate?.ratePlanCode || ''] ??
													checkoutContent?.offerDetails?.[roomRate?.orsRatePlanCode || ''])
												: undefined;
										return (
											<ShoppingCartItem
												itemTitle={itemTitle}
												key={roomType.roomTypeId + index}
												name={roomType.description ?? ''}
												cartDescription={roomRate?.ratePlanTitle ?? ''}
												roomRate={roomRate}
												numberOfPeople={getNumberOfGuests({
													t,
													numberOfAdults: numberAdults,
													numberOfChildren: numberChildren,
												})}
												price={totalRoomRatePrice}
												stayResortFees={hotelProduct?.price?.prepay?.stayResortFees}
												currencyCode={price?.total?.cash?.currencyCode ?? ''}
												taxAndFees={taxDisclaimer()}
												productType={productType}
												checkInDate={hotelProduct.checkInDate}
												checkOutDate={hotelProduct.checkOutDate}
												ppMode={booking?.ppMode}
												ppRate={hotelProduct?.roomRate?.isPreferredPartner}
												isMultiRoom={isMultiRoom}
												room={checkoutContent?.roomDetails?.[roomType?.roomTypeId || '']}
												offer={getOfferDetails({
													offerData,
													roomRate,
													price: totalRoomRatePrice,
													currencyCode: price?.total?.cash?.currencyCode,
													nights: calculateNights(checkInDate, checkOutDate),
													ppAmenities: checkoutContent?.ppAmenities,
												})}
											/>
										);
									});
								})}
							</div>
						) : null}

						{booking?.hotelProductsTaxesFeesCharges?.length ? (
							<>
								<HorizontalLine additionalClasses="border-border-subtle" />
								<div className="flex flex-col spacing-stack-m">
									{booking.hotelProductsTaxesFeesCharges.map(
										({ code, description, cash }, index) => {
											if (!cash?.amount) return null;

											return (
												<div
													className="flex flex-row justify-between spacing-inline-m"
													key={`${code}-${index}`}
												>
													<p className="text-caption">
														<span className="text-text-primary">
															<BevSafeText>{description}</BevSafeText>
														</span>
													</p>
													<p className="text-subtitle2">
														<span className="text-text-primary">
															{formatPrice({
																currencyCode: cash?.currencyCode ?? 'USD',
																value: cash?.amount,
															})}
														</span>
													</p>
												</div>
											);
										}
									)}
								</div>
							</>
						) : null}

						<HorizontalLine additionalClasses="border-border-subtle" />

						<div className="flex flex-row justify-between spacing-inline-m">
							<div className="text-body1">
								<span className="text-text-primary">{`${t('Est. Total')}*`}</span>
							</div>
							<div className="text-subtitle1">
								<span className="text-text-primary">{totalPrice}</span>
							</div>
						</div>

						{propertyBillingTotalPrice && !sameCurrencyInPrices && (
							<div className="flex flex-row justify-between">
								<p className="text-body1">
									<span className="text-text-primary">{t('Property billing currency:')}</span>
								</p>
								<p className="text-subtitle1">{propertyBillingTotalPrice}</p>
							</div>
						)}
					</div>
				</div>
			</div>
			{estimatedTotalDisclaimer ? (
				<div
					dangerouslySetInnerHTML={{ __html: estimatedTotalDisclaimer }}
					className="flex flex-col px-[20px] lg:px-[0px] pb-[20px] text-body3 tracking-[0.5px]! leading-[20px]!"
				/>
			) : null}
		</div>
	);
}
